import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { warning } from '../../../../../components/Alert/Alert';
import Input from '../../../../../components/Input/Input';
import { SelectCreate } from '../../../../../components/SelectCreate/SelectCreate';
import { useAppDispatch, useAppSelector } from '../../../../../reduxService/hooks';
import { conversionLabel } from '../../../../conversion';
import { UnitOfMeasure } from '../../../../enum/project';
import { selectProjectInfo } from '../../../../store/projectInfo/projectInfoSlice';
import { selectProject } from '../../../selector/conversion';
import { setWingsGeometry } from '../../../store/project/projectSlice';
import { cleanResults } from '../../../store/project/resultsSlice';
import { IWingsGeometryProps, IWingsGeometrySlopedWeirForm, IWingsGeometryVerticalDropForm } from './IWingsGeometry';

const heightOptions = [
    { value: 0.5, label: '0.5' },
    { value: 1, label: '1' },
    { value: 1.5, label: '1.5' },
    { value: 2, label: '2' },
    { value: 2.5, label: '2.5' },
    { value: 3, label: '3' },
];
const heightOptionsImp = [
    { value: 1.5, label: '1.5' },
    { value: 3, label: '3' },
    { value: 4.5, label: '4.5' },
    { value: 6, label: '6' },
    { value: 7.5, label: '7.5' },
    { value: 9, label: '9' },
];

const WingsGeometrySlopedWeir = (props: IWingsGeometryProps) => {
    let timer = useRef<NodeJS.Timeout | null>(null);

    const dispatch = useAppDispatch();

    const project = useAppSelector(selectProject);
    const projectInfo = useAppSelector(selectProjectInfo);

    const defaultValue: IWingsGeometrySlopedWeirForm = {
        offset: (project.data!.wingsGeometry as IWingsGeometrySlopedWeirForm).offset,
        height: (project.data!.wingsGeometry as IWingsGeometrySlopedWeirForm).height,
        width: (project.data!.wingsGeometry as IWingsGeometrySlopedWeirForm).width,
    };
    const { t } = useTranslation();
    const [form, setForm] = useState<IWingsGeometrySlopedWeirForm>(defaultValue);
    let isImperial = projectInfo?.data?.unitOfMeasure == UnitOfMeasure.Imperial;

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let name = e.target.name;
        let value = parseFloat(e.target.value);

        if (name === 'width' && value > project.data?.geometry?.channelWidth!) {
            value = project.data?.geometry?.channelWidth!;
            warning(t('warning_not_be_higher', { value: value }));
        }

        if (value < 0) {
            value = 0;
            warning(t('warning_not_be_lower', { value: 0 }));
        }

        setForm({
            ...form!,
            [name]: value,
        });
    };

    useEffect(() => {
        if (!!project.data?.geometry?.channelWidth && project.data?.geometry?.channelWidth < form.width)
            setForm({
                ...form!,
                width: project.data?.geometry?.channelWidth,
            });
    }, [project.data?.geometry?.channelWidth]);

    useEffect(() => {
        if (timer.current) {
            clearTimeout(timer.current);
        }
        timer.current = setTimeout(() => {
            dispatch(cleanResults());
            dispatch(setWingsGeometry(form as IWingsGeometryVerticalDropForm));
        }, 1000);
    }, [form]);

    return (
        <div className='d-flex flex-column gap-2'>
            <Input
                type='number'
                title={t('input_crest_width', { unit_measure: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number] })}
                name='width'
                value={form.width}
                className='d-flex justify-content-between align-items-center'
                onChange={handleInputChange}
            />
            <Input
                type='number'
                title={t('input_layerOffset', { unit_measure: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number] })}
                value={form.offset}
                name='offset'
                className='d-flex justify-content-between align-items-center'
                onChange={handleInputChange}
            />
            <div className='d-flex justify-content-between align-items-center'>
                <label htmlFor='height'>{t('input_height', { unit_measure: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number] })} </label>
                <SelectCreate
                    value={form.height}
                    name='height'
                    options={isImperial ? heightOptionsImp : heightOptions}
                    parentOnChange={(newValue: any) =>
                        setForm({
                            ...form!,
                            height: newValue,
                        })
                    }
                />
            </div>
        </div>
    );
};

export default WingsGeometrySlopedWeir;
